import '@fortawesome/fontawesome-svg-core/styles.css';
import '@/styles/tailwind.css';

import { UserProvider } from '@auth0/nextjs-auth0/client';
import { config } from '@fortawesome/fontawesome-svg-core';
import Hotjar from '@hotjar/browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HydrationBoundary } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import getConfig from 'next/config';
import NextHead from 'next/head';
import PropTypes from 'prop-types';
import { cloneElement, useEffect } from 'react';
import { CookiesProvider } from 'react-cookie';
import { I18nextProvider, useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

import { DEFAULT_COOKIE_OPTIONS } from '@/constants/Cookies';
import {
  createCookieName,
  FeatureFlags,
  isFeatureFlagEnabledInEnv,
} from '@/hooks/useFeatureFlag';
import i18n from '@/i18n/index';
import Layout from '@/layouts/index';
import { GlobalStyle } from '@/styles/GlobalStyle';
import { ThemeProvider } from '@/ui/ThemeProvider';
import { Toaster } from '@/ui/Toast';

import { AnnouncementModalProvider } from '../context/AnnouncementModalContext';

const isServer = () => typeof window === 'undefined';

const cookies = new Cookies();

if (!isServer()) {
  window.FeatureFlags = FeatureFlags;

  window.setFeatureFlag = (featureFlagName, value) => {
    cookies.set(
      createCookieName(featureFlagName),
      value,
      DEFAULT_COOKIE_OPTIONS,
    );
    window.getCurrentFeatureFlagConfiguration();
  };

  window.getCurrentFeatureFlagConfiguration = () => {
    // eslint-disable-next-line no-console
    console.table(
      Object.entries(FeatureFlags).reduce(
        (acc, [constant, featureFlagName]) => {
          const forcedByEnv = isFeatureFlagEnabledInEnv(featureFlagName);
          const enabledByCookie =
            cookies.get(createCookieName(featureFlagName)) === 'true' ||
            cookies.get(createCookieName(featureFlagName)) === true;

          return {
            ...acc,
            [`FeatureFlags.${constant}`]: {
              enabled: forcedByEnv || enabledByCookie ? '✅' : '🚫',
              source: forcedByEnv
                ? 'env (all users)'
                : enabledByCookie
                  ? 'cookie'
                  : '-',
            },
          };
        },
        {},
      ),
    );
  };
}

const ContextProvider = ({ providers, children }) => {
  return providers.reverse().reduce((AccumulatedProviders, current) => {
    const [CurrentProvider, currentProps] = Array.isArray(current)
      ? current
      : [current, {}];
    // eslint-disable-next-line react/prop-types
    return (
      <CurrentProvider {...currentProps}>
        {AccumulatedProviders}
      </CurrentProvider>
    );
  }, children);
};

ContextProvider.propTypes = {
  providers: PropTypes.array,
  children: PropTypes.node,
};

config.autoAddCss = false;

const Head = () => {
  const { t } = useTranslation();

  return (
    <NextHead>
      <meta
        key="viewport"
        name="viewport"
        content="initial-scale=1.0, width=device-width"
      />
      <title key="title">UiTdatabank</title>
      <meta name="description" content={t('description')} />
    </NextHead>
  );
};

const queryClient = new QueryClient();

const App = ({ Component, pageProps, children }) => {
  const { publicRuntimeConfig } = getConfig();
  useEffect(() => {
    if (isServer()) return;
    Hotjar.init(181435, 6);
  }, []);

  return (
    <>
      <Head />
      <UserProvider />
      <ContextProvider
        providers={[
          [I18nextProvider, { i18n }],
          ThemeProvider,
          [
            CookiesProvider,
            {
              cookies: isServer()
                ? new Cookies(pageProps.cookies ?? '')
                : undefined,
            },
          ],
          [QueryClientProvider, { client: queryClient }],
          [HydrationBoundary, { state: pageProps?.dehydratedState ?? {} }],
          AnnouncementModalProvider,
        ]}
      >
        <GlobalStyle />
        <Toaster position="top-right" />
        {publicRuntimeConfig?.environment === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
        <Layout>
          {children ? (
            cloneElement(children, { ...children.props, ...pageProps })
          ) : (
            <Component {...pageProps} />
          )}
        </Layout>
      </ContextProvider>
    </>
  );
};

App.propTypes = {
  Component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  pageProps: PropTypes.object,
  children: PropTypes.node,
};

App.getInitialProps = async ({ ctx }) => {
  const serverCookies = new Cookies(ctx?.req?.headers?.cookie);
  const language = serverCookies.get('udb-language') || 'nl';

  if (isServer()) await i18n.changeLanguage(language);

  return { pageProps: { language } };
};

export default App;
